import { useEffect, useMemo, useRef, useState } from "react";
import { mdiChevronLeft, mdiChevronRight } from "@mdi/js";
import {
  isValid,
  endOfDay,
  getDay,
  getDaysInMonth,
  isAfter,
  isBefore,
  startOfDay,
  startOfMonth,
  isSameDay,
} from "date-fns";
import {
  cn,
  getBackgroundStyles,
  getForegroundStyles,
  getBorderStyles,
  getFocusRingStyles,
} from "../../styles";
import { PanelColor, ThemeColor } from "../../props";
import { Button, Typography } from "../../atoms";
import { useTranslate } from "../../util/localization";

const DAY_SIZE = 35;
const CALENDAR_WIDTH = DAY_SIZE * 7 + 32; // 7 columns + mx-4 padding
const BASE_YEAR = 1950;
const NUM_YEARS = 200;

interface CalendarProps {
  className?: string;
  value?: Date;
  onChange?: (date: Date) => void;
  dateOnly?: boolean;
  onSelect?: (date: Date) => void;
  minValue?: Date;
  maxValue?: Date;
  rangeBeginValue?: Date;
  rangeEndValue?: Date;
  color?: PanelColor;
}

const yearOptions = [...Array(NUM_YEARS)].map((_, offset) => ({
  value: BASE_YEAR + offset,
  label: (BASE_YEAR + offset).toString(),
}));

const hourOptions = [...Array(24)].map((_, value) => ({
  value,
  label: value.toString().padStart(2, "0"),
}));

const minuteOptions = [...Array(60)].map((_, value) => ({
  value,
  label: value.toString().padStart(2, "0"),
}));

interface DayIndicatorProps {
  dayIndex?: number;
  disabled?: boolean;
  selected?: boolean;
  inRange?: boolean;
  isCap?: boolean;
  isRangeEnd?: boolean;
  isRangeBegin?: boolean;
  onClick?: () => void;
  color?: PanelColor;
}

function DayIndicator({
  dayIndex,
  disabled = false,
  selected = false,
  inRange = false,
  isCap = false,
  isRangeEnd = false,
  isRangeBegin = false,
  onClick,
  color = PanelColor.level0,
}: DayIndicatorProps) {
  const hasDay = dayIndex != null;

  return (
    <div
      style={{ width: DAY_SIZE, height: DAY_SIZE }}
      className={cn(
        "relative inline-flex cursor-pointer rounded-full select-none",
        "hover:bg-primary-100 hover:text-primary-900",
        disabled && "cursor-default hover:bg-transparent hover:text-current",
        // Range background styling
        (inRange || (selected && (isRangeEnd || isRangeBegin)) || isCap) && [
          "before:content-[''] before:absolute before:top-0.5 before:bottom-0.5",
          "before:left-0 before:right-0 before:bg-primary-100",
          (isRangeBegin ? selected : isCap) && "before:rounded-l-full",
          (isRangeBegin ? isCap : selected) && "before:rounded-r-full",
        ],
      )}
      onClick={!disabled ? onClick : undefined}
    >
      {hasDay && (
        <div
          className={cn(
            "w-full h-full flex items-center justify-center z-10 rounded-full",
            selected && [
              "bg-primary-500 text-front-50 dark:bg-primary-600",
              isRangeEnd && "rounded-l rounded-r-full",
              isRangeBegin && "rounded-r rounded-l-full",
            ],
            disabled && "text-gray-400",
          )}
        >
          <Typography.Span
            className={cn(
              selected && "text-front-50 font-medium",
              disabled && "text-gray-400",
            )}
          >
            {(dayIndex + 1).toString()}
          </Typography.Span>
        </div>
      )}
    </div>
  );
}

export function Calendar({
  className,
  value,
  onChange,
  dateOnly = false,
  onSelect,
  minValue,
  maxValue,
  rangeBeginValue,
  rangeEndValue,
  color = PanelColor.level0,
}: CalendarProps) {
  const [t, { localDateFns }] = useTranslate();
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Create month options with proper labels
  const monthOptions = useMemo(
    () =>
      [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ].map((label, value) => ({ value, label })),
    [],
  );

  const weekDayLabels = [...Array(7).keys()].map((index) =>
    localDateFns.localize.day((index + localDateFns.options.weekStartsOn) % 7, {
      width: "short",
    }),
  );
  //["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

  const validValue = isValid(value) ? value : new Date();

  // Internal state
  const [year, setYear] = useState(validValue.getFullYear());
  const [displayYear, setDisplayYear] = useState(validValue.getFullYear());
  const [month, setMonth] = useState(validValue.getMonth());
  const [displayMonth, setDisplayMonth] = useState(validValue.getMonth());
  const [day, setDay] = useState(validValue.getDate());
  const [hour, setHour] = useState(validValue.getHours());
  const [minute, setMinute] = useState(validValue.getMinutes());
  const [monthStack, setMonthStack] = useState([
    {
      id: `${displayYear}-${displayMonth}`,
      date: new Date(displayYear, displayMonth),
      isVisible: true,
      direction: "none" as "left" | "right" | "none",
    },
  ]);

  const displayDate = new Date(displayYear, displayMonth);

  useEffect(() => {
    if (onChangeRef.current) {
      onChangeRef.current(new Date(year, month, day, hour, minute));
    }
  }, [year, month, day, hour, minute]);

  // Handlers
  const jumpDisplayMonth = (newYear: number, newMonth: number) => {
    setMonthStack([
      {
        id: `${newYear}-${newMonth}`,
        date: new Date(newYear, newMonth),
        isVisible: true,
        direction: "none",
      },
    ]);
  };

  const handleChangeYear = (newYear: number) => {
    if (!rangeBeginValue && !rangeEndValue) {
      const maxDay = getDaysInMonth(new Date(newYear, month));
      setDay((d) => Math.min(d, maxDay));
      setYear(newYear);
    }
    setDisplayYear(newYear);
    jumpDisplayMonth(newYear, displayMonth);
  };

  const handleChangeMonth = (newMonth: number) => {
    if (!rangeBeginValue && !rangeEndValue) {
      const maxDay = getDaysInMonth(new Date(year, newMonth));
      setDay((d) => Math.min(d, maxDay));
      setMonth(newMonth);
    }
    setDisplayMonth(newMonth);
    jumpDisplayMonth(displayYear, newMonth);
  };

  const handleDecDisplayMonth = () => {
    // Calculate previous month
    const newYear = displayMonth === 0 ? displayYear - 1 : displayYear;
    const newMonth = displayMonth === 0 ? 11 : displayMonth - 1;
    const newDate = new Date(newYear, newMonth);

    // Update display state
    setDisplayYear(newYear);
    setDisplayMonth(newMonth);

    // Add new month to stack
    setMonthStack((prev) => [
      ...prev,
      {
        id: `${newYear}-${newMonth}`,
        date: newDate,
        isVisible: false, // Start invisible for mount animation
        direction: "left" as const,
      },
    ]);

    // Trigger mount animation
    requestAnimationFrame(() => {
      setMonthStack((prev) =>
        prev.map((item, index) =>
          index === prev.length - 1 ? { ...item, isVisible: true } : item,
        ),
      );
    });

    // Remove bottom page after animation
    setTimeout(() => {
      setMonthStack((prev) => (prev.length > 1 ? prev.slice(1) : prev));
    }, 700);
  };

  const handleIncDisplayMonth = () => {
    // Calculate next month
    const newYear = displayMonth === 11 ? displayYear + 1 : displayYear;
    const newMonth = displayMonth === 11 ? 0 : displayMonth + 1;
    const newDate = new Date(newYear, newMonth);

    // Update display state
    setDisplayYear(newYear);
    setDisplayMonth(newMonth);

    // Add new month to stack
    setMonthStack((prev) => [
      ...prev,
      {
        id: `${newYear}-${newMonth}`,
        date: newDate,
        isVisible: false, // Start invisible for mount animation
        direction: "right" as const,
      },
    ]);

    // Trigger mount animation
    requestAnimationFrame(() => {
      setMonthStack((prev) =>
        prev.map((item, index) =>
          index === prev.length - 1 ? { ...item, isVisible: true } : item,
        ),
      );
    });

    // Remove bottom page after animation
    setTimeout(() => {
      setMonthStack((prev) => (prev.length > 1 ? prev.slice(1) : prev));
    }, 700);
  };

  const handleDaySelect = (day) => {
    setYear(displayYear);
    setMonth(displayMonth);
    setDay(day);
    if (dateOnly && onSelect) {
      onSelect(new Date(displayYear, displayMonth, day));
    }
  };

  const isDayDisabled = (day) => {
    let date = new Date(displayYear, displayMonth, day, hour, minute);
    if (dateOnly) {
      date = startOfDay(date);
    }
    return (
      (minValue && isBefore(endOfDay(date), minValue)) ||
      (maxValue && isAfter(startOfDay(date), maxValue))
    );
  };

  const current = new Date(year, month, day, hour, minute);

  const isDayInRange = (day) => {
    let date = new Date(displayYear, displayMonth, day, hour, minute);
    if (
      rangeBeginValue &&
      isAfter(date, rangeBeginValue) &&
      isBefore(date, current)
    )
      return true;
    if (
      rangeEndValue &&
      isBefore(date, rangeEndValue) &&
      isAfter(date, current)
    )
      return true;
    return false;
  };

  const isDayCap = (day) => {
    let date = new Date(displayYear, displayMonth, day, hour, minute);
    if (rangeBeginValue && isSameDay(date, rangeBeginValue)) return true;
    if (rangeEndValue && isSameDay(date, rangeEndValue)) return true;
    return false;
  };

  const daysInDisplayMonth = getDaysInMonth(displayDate);
  const firstDayOfWeek = getDay(startOfMonth(displayDate));
  const emptyDaysCount = firstDayOfWeek > 0 ? firstDayOfWeek : 0;

  // Helper function to render month labels
  const renderMonthLabel = (monthDate: Date) => {
    return (
      <div
        className="flex justify-center items-center h-full"
        style={{ width: DAY_SIZE * 7 }}
      >
        <Typography.H4 className="font-medium m-0">
          {`${monthOptions[monthDate.getMonth()].label} ${monthDate.getFullYear()}`}
        </Typography.H4>
      </div>
    );
  };

  // Helper function to render a month's days
  const renderMonthDays = (monthDate: Date, isCurrentMonth: boolean = true) => {
    const daysInMonth = getDaysInMonth(monthDate);
    const firstDayOfMonth = getDay(startOfMonth(monthDate));
    const emptyDays =
      firstDayOfMonth >= localDateFns.options.weekStartsOn
        ? firstDayOfMonth - localDateFns.options.weekStartsOn
        : 7 - (localDateFns.options.weekStartsOn - firstDayOfMonth);

    return (
      <div className="flex flex-wrap" style={{ width: DAY_SIZE * 7 }}>
        {/* Empty days at start of month */}
        {[...Array(emptyDays)].map((_, index) => (
          <DayIndicator key={`empty-${index}`} disabled color={color} />
        ))}

        {/* Days of month */}
        {[...Array(daysInMonth)].map((_, dayIndex) => (
          <DayIndicator
            key={dayIndex}
            dayIndex={dayIndex}
            disabled={!isCurrentMonth || isDayDisabled(dayIndex + 1)}
            selected={
              isCurrentMonth &&
              year === displayYear &&
              month === displayMonth &&
              dayIndex === day - 1
            }
            onClick={() =>
              isCurrentMonth &&
              !isDayDisabled(dayIndex + 1) &&
              handleDaySelect(dayIndex + 1)
            }
            inRange={isCurrentMonth && isDayInRange(dayIndex + 1)}
            isCap={isCurrentMonth && isDayCap(dayIndex + 1)}
            isRangeEnd={!!rangeBeginValue}
            isRangeBegin={!!rangeEndValue}
            color={color}
          />
        ))}
      </div>
    );
  };

  // Render a month with 3D mount animation
  const renderStackMonth = (stackItem, index) => {
    const { date, isVisible, direction } = stackItem;
    const isCurrentMonth =
      date.getFullYear() === displayYear && date.getMonth() === displayMonth;

    const get3DTransform = () => {
      if (isVisible) {
        return "perspective(1200px) rotateY(0deg) translateZ(0)";
      }

      // Direction-based folding
      if (direction === "left") {
        // Previous month comes from left edge
        return "perspective(1200px) rotateY(-90deg) translateZ(-20px)";
      } else if (direction === "right") {
        // Next month comes from right edge
        return "perspective(1200px) rotateY(90deg) translateZ(-20px)";
      }

      return "perspective(1200px) rotateY(0deg) translateZ(0)";
    };

    const getTransformOrigin = () => {
      if (direction === "left") {
        return "left center"; // Fold from left edge
      } else if (direction === "right") {
        return "right center"; // Fold from right edge
      }
      return "center center";
    };

    return (
      <div
        key={stackItem.id}
        className={cn(
          "absolute inset-0 transition-all duration-700 ease-out",
          getBackgroundStyles(color),
        )}
        style={{
          transform: get3DTransform(),
          transformOrigin: getTransformOrigin(),
          opacity: isVisible ? 1 : 0.1,
          zIndex: index + 1, // Latest month on top
          transformStyle: "preserve-3d",
        }}
      >
        {/* Month label */}
        <div className="flex justify-center items-center h-8">
          <Typography.H4 className="font-medium m-0 text-sm">
            {`${monthOptions[date.getMonth()].label} ${date.getFullYear()}`}
          </Typography.H4>
        </div>

        {/* Days grid — offset down by DAY_SIZE to leave room for static weekday labels */}
        <div style={{ height: DAY_SIZE * 6, marginTop: DAY_SIZE }}>
          {renderMonthDays(date, isCurrentMonth)}
        </div>
      </div>
    );
  };

  return (
    <div
      style={{ minWidth: CALENDAR_WIDTH }}
      className={cn(
        "flex flex-col items-center",
        getBackgroundStyles(color),
        getForegroundStyles(color),
        className,
      )}
    >
      {/* Header */}
      <div
        className={cn(
          "self-stretch flex justify-between items-end p-3",
          "bg-primary-500 text-front-50 dark:bg-primary-600",
        )}
      >
        <div className="flex flex-col">
          <select
            value={year}
            onChange={(e) => handleChangeYear(Number(e.target.value))}
            className={cn(
              "text-sm w-32 bg-transparent border-none outline-none cursor-pointer appearance-none",
              "text-front-50",
            )}
          >
            {yearOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <div className="flex items-center -mt-2">
            <Typography.Span className="text-lg font-medium pr-2 w-8">
              {day}.
            </Typography.Span>
            <select
              value={month}
              onChange={(e) => handleChangeMonth(Number(e.target.value))}
              className={cn(
                "text-lg w-32 bg-transparent border-none outline-none cursor-pointer appearance-none",
                "text-front-50",
              )}
            >
              {monthOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {!dateOnly && (
          <div className="flex items-center text-2xl  pb-1">
            <select
              value={hour}
              onChange={(e) => setHour(Number(e.target.value))}
              className={cn(
                "w-8 bg-transparent border-none outline-none cursor-pointer appearance-none text-right",
                "text-front-50",
              )}
            >
              {hourOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <span className="mx-0.5">:</span>
            <select
              value={minute}
              onChange={(e) => setMinute(Number(e.target.value))}
              className={cn(
                "w-8 bg-transparent border-none outline-none cursor-pointer appearance-none text-left",
                "text-front-50",
              )}
            >
              {minuteOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Calendar grid */}
      <div className="mx-4" style={{ width: DAY_SIZE * 7 }}>
        {/* Calendar container */}
        <div
          className="relative overflow-hidden"
          style={{ height: DAY_SIZE * 7 + 32 }} // month label + weekday labels + 6 day rows
        >
          {monthStack.map((stackItem, index) =>
            renderStackMonth(stackItem, index),
          )}

          {/* Weekday labels — static, not animated */}
          <div
            className="absolute left-0 right-0 flex z-40"
            style={{ top: 32 }}
          >
            {weekDayLabels.map((dayLabel, index) => (
              <div
                key={index}
                style={{ width: DAY_SIZE, height: DAY_SIZE }}
                className="inline-flex items-center justify-center cursor-default select-none"
              >
                <Typography.Span muted className="text-xs">
                  {dayLabel}
                </Typography.Span>
              </div>
            ))}
          </div>

          {/* Absolutely positioned navigation buttons */}
          <Button
            iconPath={mdiChevronLeft}
            onClick={handleDecDisplayMonth}
            color={ThemeColor.none}
            dense
            className="absolute left-0 top-4 transform -translate-y-1/2 z-50 bg-white"
          />

          <Button
            iconPath={mdiChevronRight}
            onClick={handleIncDisplayMonth}
            color={ThemeColor.none}
            dense
            className="absolute right-0 top-4 transform -translate-y-1/2 z-50 bg-white"
          />
        </div>
      </div>
    </div>
  );
}
